// InvoicedColumns — what the contract was billed, month by month, as a single
// brand-violet column series.
//
// Three deliberate choices:
//   - The y-axis is money, and money is the point: ticks are abbreviated
//     because a tick has no room for cents, while the tooltip and the detail
//     table below carry the exact figures.
//   - Every month on the axis draws a column, including a nought. There is no
//     unmeasured month here — the composer emits a row for every month in the
//     window, and a nought means nobody billed, which is a fact worth seeing
//     rather than a gap. A minimum point size keeps that nought visible.
//   - The series is brand violet, the palette's "metric the reader is looking
//     at". Invoiced is the one metric this chart is about; the approved split
//     belongs to the tooltip and the table, not to a second series that would
//     turn a single-subject chart into a breakdown.
//
// Recharts' accessibility layer is switched OFF deliberately. It wraps the
// plot in a focusable role="application" svg, which inside the labelled image
// is a tab stop that announces nothing; the label plus the table below are the
// accessible path to the same figures.

import type { JSX } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "../../../../contexts/ThemeContext";
import {
  PRIMARY_LINE_COLOUR,
  TICK_FONT_SIZE,
  getGridColour,
  getTickColour,
  getTooltipContentStyle,
  useReducedMotionAwareAnimationDuration,
} from "../../../../lib/charts/chartTokens";
import { formatAudCompact } from "../../../../utils/formatCurrency";
import { formatReportMonthShort } from "../reportFormat";
import { describeInvoicedChart, formatInvoicedTooltip } from "./invoicedTooltip";
import type {
  FinancialMonthRow,
  FinancialReportModel,
} from "../../../../types/report";

const CHART_HEIGHT = 240;

/** Minimum drawn height, in pixels, for a month nobody billed in. */
const MIN_BAR_HEIGHT = 2;

type GstBasis = FinancialReportModel["gstBasis"];

export interface InvoicedTooltipCardProps {
  active?: boolean;
  payload?: ReadonlyArray<{ payload: FinancialMonthRow }>;
  isDark: boolean;
  gstBasis: GstBasis;
}

/** Exported so the card can be tested by direct call: Recharts will not
 *  render it under jsdom, and an untested hover card is an untested string. */
export function InvoicedTooltipCard({
  active,
  payload,
  isDark,
  gstBasis,
}: InvoicedTooltipCardProps): JSX.Element | null {
  if (!active || !payload || payload.length === 0) return null;
  const style = getTooltipContentStyle(isDark);
  return (
    <div style={{ ...style, padding: "8px 10px" }}>
      {formatInvoicedTooltip(payload[0].payload, gstBasis)}
    </div>
  );
}

export interface InvoicedColumnsProps {
  /** Ascending by month, one entry per month in the window including the
   *  months nobody billed in. Never empty: the composer draws one row per
   *  window position whatever the records say. */
  monthly: FinancialMonthRow[];
  gstBasis: GstBasis;
}

export default function InvoicedColumns({
  monthly,
  gstBasis,
}: InvoicedColumnsProps): JSX.Element {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const animationDuration = useReducedMotionAwareAnimationDuration();

  const gridColour = getGridColour(isDark);
  const tickColour = getTickColour(isDark);
  const seriesLabel = `Invoiced, ${gstBasis}`;

  return (
    <div>
      <div
        role="img"
        aria-label={describeInvoicedChart(monthly, gstBasis)}
        data-testid="financial-invoiced-chart"
        style={{ width: "100%", height: CHART_HEIGHT }}
      >
        <ResponsiveContainer>
          <BarChart
            data={monthly}
            margin={{ top: 8, right: 16, bottom: 0, left: 4 }}
            accessibilityLayer={false}
          >
            <CartesianGrid
              stroke={gridColour}
              vertical={false}
              strokeDasharray="0"
            />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: tickColour, fontSize: TICK_FONT_SIZE }}
              tickFormatter={formatReportMonthShort}
              interval="preserveStartEnd"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: tickColour, fontSize: TICK_FONT_SIZE }}
              width={64}
              tickFormatter={formatAudCompact}
            />
            <Tooltip
              content={(props) => (
                <InvoicedTooltipCard
                  {...(props as {
                    active?: boolean;
                    payload?: ReadonlyArray<{ payload: FinancialMonthRow }>;
                  })}
                  isDark={isDark}
                  gstBasis={gstBasis}
                />
              )}
              cursor={{ fill: gridColour }}
            />
            <Bar
              dataKey="invoicedTotal"
              name={seriesLabel}
              fill={PRIMARY_LINE_COLOUR}
              minPointSize={MIN_BAR_HEIGHT}
              isAnimationActive={animationDuration !== 0}
              animationDuration={animationDuration}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p
        data-testid="financial-invoiced-chart-legend"
        className="mt-2 flex flex-wrap items-center gap-4 text-caption text-slate-600 dark:text-slate-400"
      >
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 rounded-sm"
            style={{ background: PRIMARY_LINE_COLOUR }}
          />
          {seriesLabel}
        </span>
      </p>
    </div>
  );
}
